import React, { Suspense, lazy as reactLazy, Component } from 'react';
import { Toaster } from 'sonner';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import './App.css';

// Tiny redirect helper to map /tienda/producto/:id → /distribuidor/producto/:id
const LegacyTiendaProductRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/distribuidor/producto/${id}`} replace />;
};

// Error Boundary to prevent blank screens from uncaught errors
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, showDetails: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary] Error capturado:', error);
    console.error('[ErrorBoundary] Info:', errorInfo);
    this.setState({ errorInfo });
    // Store error for debugging
    try {
      localStorage.setItem('last_error', JSON.stringify({
        message: error?.message || 'Unknown error',
        stack: error?.stack?.substring(0, 500) || '',
        time: new Date().toISOString(),
        url: window.location.href
      }));
    } catch (e) { /* ignore localStorage errors */ }
  }

  render() {
    if (this.state.hasError) {
      const errorMsg = this.state.error?.message || 'Error desconocido';
      return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50 p-4">
          <div className="text-center max-w-md w-full">
            <div className="text-5xl mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-16 h-16 mx-auto text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Algo salió mal</h2>
            <p className="text-gray-600 mb-4">Ha ocurrido un error inesperado. Intenta recargar la página.</p>
            <div className="space-y-2">
              <button
                onClick={() => {
                  window.location.reload();
                }}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                data-testid="error-reload-button"
              >
                Recargar Página
              </button>
              <button
                onClick={() => {
                  this.setState({ hasError: false, error: null, errorInfo: null });
                  window.location.href = '/dashboard';
                }}
                className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                data-testid="error-go-home-button"
              >
                Ir al Inicio
              </button>
              <button
                onClick={() => this.setState(prev => ({ showDetails: !prev.showDetails }))}
                className="w-full px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 underline"
              >
                {this.state.showDetails ? 'Ocultar detalles' : 'Ver detalles del error'}
              </button>
              {this.state.showDetails && (
                <div className="mt-2 p-3 bg-gray-100 rounded-lg text-left text-xs text-gray-600 max-h-40 overflow-y-auto break-all">
                  <p className="font-medium text-red-600">{errorMsg}</p>
                  {this.state.error?.stack && (
                    <pre className="mt-1 whitespace-pre-wrap text-[10px]">{this.state.error.stack.substring(0, 300)}</pre>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Page-level Error Boundary - more granular recovery for individual pages
class PageErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[PageError] Componente:', this.props.pageName || 'unknown', error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center min-h-[60vh] p-4">
          <div className="text-center max-w-sm">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-12 h-12 mx-auto text-amber-500 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <h3 className="text-lg font-semibold text-gray-800 mb-1">Error en esta sección</h3>
            <p className="text-sm text-gray-500 mb-4">
              {this.state.error?.message || 'Ocurrió un error inesperado'}
            </p>
            <button
              onClick={() => {
                this.setState({ hasError: false, error: null });
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm mr-2"
            >
              Reintentar
            </button>
            <button
              onClick={() => { window.location.href = '/dashboard'; }}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
            >
              Ir al Inicio
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Export PageErrorBoundary for use in other components
export { PageErrorBoundary };

// Reload once if a page chunk fails to load (stale bundle after a new deploy)
const lazy = (factory) => reactLazy(() =>
  factory().then((mod) => {
    sessionStorage.removeItem('chunk_reload');
    return mod;
  }).catch((err) => {
    if (!sessionStorage.getItem('chunk_reload')) {
      sessionStorage.setItem('chunk_reload', '1');
      window.location.reload();
      return new Promise(() => {});
    }
    throw err;
  })
);

// Lazy load pages
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const SalesPage = lazy(() => import('./pages/SalesPage'));
const ProductsPage = lazy(() => import('./pages/ProductsPage'));
const InventoryPage = lazy(() => import('./pages/InventoryPage'));
const ConfigPage = lazy(() => import('./pages/ConfigPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const ClientsPage = lazy(() => import('./pages/ClientsPage'));
const VendedoresPage = lazy(() => import('./pages/VendedoresPage'));
const DevicesPage = lazy(() => import('./pages/DevicesPage'));
const UsersPage = lazy(() => import('./pages/UsersPage'));
const TiendaPage = lazy(() => import('./pages/TiendaPage'));
const PortafolioPage = lazy(() => import('./pages/PortafolioPage'));
const PortafolioAdmin = lazy(() => import('./pages/PortafolioAdmin'));
const TiendaProductPage = lazy(() => import('./pages/TiendaProductPage'));
const TiendaCheckoutPage = lazy(() => import('./pages/TiendaCheckoutPage'));
const CatalogoPage = lazy(() => import('./pages/CatalogoPage'));
const CatalogoProductPage = lazy(() => import('./pages/CatalogoProductPage'));
const CatalogoListPage = lazy(() => import('./pages/CatalogoListPage'));
const ClientesPage = lazy(() => import('./pages/ClientesPage'));
const ClientesProductPage = lazy(() => import('./pages/ClientesProductPage'));
const ClientesCartPage = lazy(() => import('./pages/ClientesCartPage'));

const Loading = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-gray-600">Cargando...</p>
    </div>
  </div>
);

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <Loading />;
  }
  
  return user ? children : <Navigate to="/login" />;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <Loading />;
  }
  
  // Allow /register to stay even when authenticated (for post-register personalization)
  const isRegister = window.location.pathname === '/register';
  if (user && !isRegister) {
    return <Navigate to="/dashboard" />;
  }
  
  return !user || isRegister ? children : <Navigate to="/dashboard" />;
};

function AppRoutes() {
  return (
    <Suspense fallback={<Loading />}>
      <Toaster richColors position="top-center" />
      <Routes>
        <Route path="/login" element={
          <PublicRoute>
            <PageErrorBoundary pageName="Login"><LoginPage /></PageErrorBoundary>
          </PublicRoute>
        } />
        <Route path="/portafolio" element={<PageErrorBoundary pageName="Portafolio"><PortafolioPage /></PageErrorBoundary>} />
        <Route path="/portafolio/admin" element={<PageErrorBoundary pageName="PortafolioAdmin"><PortafolioAdmin /></PageErrorBoundary>} />
        <Route path="/distribuidor" element={<PageErrorBoundary pageName="Distribuidor"><TiendaPage /></PageErrorBoundary>} />
        <Route path="/distribuidor/producto/:id" element={<PageErrorBoundary pageName="DistribuidorProducto"><TiendaProductPage /></PageErrorBoundary>} />
        <Route path="/distribuidor/cotizacion" element={<PageErrorBoundary pageName="DistribuidorCotizacion"><TiendaCheckoutPage /></PageErrorBoundary>} />
        <Route path="/catalogo" element={<PageErrorBoundary pageName="Catalogo"><CatalogoPage /></PageErrorBoundary>} />
        <Route path="/catalogo/producto/:id" element={<PageErrorBoundary pageName="CatalogoProducto"><CatalogoProductPage /></PageErrorBoundary>} />
        <Route path="/catalogo/lista" element={<PageErrorBoundary pageName="CatalogoLista"><CatalogoListPage /></PageErrorBoundary>} />
        <Route path="/clientes" element={<PageErrorBoundary pageName="Clientes"><ClientesPage /></PageErrorBoundary>} />
        <Route path="/clientes/producto/:id" element={<PageErrorBoundary pageName="ClientesProducto"><ClientesProductPage /></PageErrorBoundary>} />
        <Route path="/clientes/carrito" element={<PageErrorBoundary pageName="ClientesCarrito"><ClientesCartPage /></PageErrorBoundary>} />
        {/* Legacy /tienda routes — redirect to /distribuidor to keep old links alive */}
        <Route path="/tienda" element={<Navigate to="/distribuidor" replace />} />
        <Route path="/tienda/producto/:id" element={<LegacyTiendaProductRedirect />} />
        <Route path="/tienda/checkout" element={<Navigate to="/distribuidor/cotizacion" replace />} />
        <Route path="/register" element={
          <PublicRoute>
            <PageErrorBoundary pageName="Register"><RegisterPage /></PageErrorBoundary>
          </PublicRoute>
        } />
        <Route path="/dashboard" element={
          <PrivateRoute>
            <PageErrorBoundary pageName="Dashboard"><DashboardPage /></PageErrorBoundary>
          </PrivateRoute>
        } />
        <Route path="/vender" element={
          <PrivateRoute>
            <PageErrorBoundary pageName="Sales"><SalesPage /></PageErrorBoundary>
          </PrivateRoute>
        } />
        <Route path="/productos" element={
          <PrivateRoute>
            <PageErrorBoundary pageName="Products"><ProductsPage /></PageErrorBoundary>
          </PrivateRoute>
        } />
        <Route path="/inventario" element={
          <PrivateRoute>
            <PageErrorBoundary pageName="Inventory"><InventoryPage /></PageErrorBoundary>
          </PrivateRoute>
        } />
        <Route path="/configuracion" element={
          <PrivateRoute>
            <PageErrorBoundary pageName="Config"><ConfigPage /></PageErrorBoundary>
          </PrivateRoute>
        } />
        <Route path="/perfil" element={
          <PrivateRoute>
            <PageErrorBoundary pageName="Profile"><ProfilePage /></PageErrorBoundary>
          </PrivateRoute>
        } />
        <Route path="/admin" element={
          <PrivateRoute>
            <PageErrorBoundary pageName="Admin"><AdminPanel /></PageErrorBoundary>
          </PrivateRoute>
        } />
        <Route path="/gestion/clientes" element={
          <PrivateRoute>
            <PageErrorBoundary pageName="Clients"><ClientsPage /></PageErrorBoundary>
          </PrivateRoute>
        } />
        <Route path="/vendedores" element={
          <PrivateRoute>
            <PageErrorBoundary pageName="Vendedores"><VendedoresPage /></PageErrorBoundary>
          </PrivateRoute>
        } />
        {/* Legacy redirect — old internal route renamed to free /clientes for public store */}
        <Route path="/clientes/admin" element={<Navigate to="/gestion/clientes" replace />} />
        <Route path="/dispositivos" element={
          <PrivateRoute>
            <PageErrorBoundary pageName="Devices"><DevicesPage /></PageErrorBoundary>
          </PrivateRoute>
        } />
        <Route path="/usuarios" element={
          <PrivateRoute>
            <PageErrorBoundary pageName="Users"><UsersPage /></PageErrorBoundary>
          </PrivateRoute>
        } />
        <Route path="/" element={<Navigate to="/dashboard" />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
